/* Feb-2026 · Author-facing Bowker Wizard.
 *
 * Renders inside `IsbnManagerDialog` as an alternate editor mode.
 * Steps a user through metadata capture in the exact order Bowker
 * needs for title registration:
 *   1. Basics (title, subtitle, contributors, format)
 *   2. Physical (trim, page count, edition, language)
 *   3. Categories (BISAC + publication date)
 *   4. Retail (price + currency)
 *   5. Cover (JPG/PNG upload, max 2 MB)
 *
 * Wizard state lives in a single `draft` object and each Next click
 * PATCHes the current step's fields via `PUT /isbn/mine/{id}`. Cover
 * upload uses `POST /isbn/mine/{id}/cover`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ChevronLeft, ChevronRight, Upload, X, Check, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { isbnApi } from '@/lib/api';

const STEPS = [
  { key: 'basics',      label: 'Basics'     },
  { key: 'physical',    label: 'Physical'   },
  { key: 'categories',  label: 'Categories' },
  { key: 'retail',      label: 'Retail'     },
  { key: 'cover',       label: 'Cover'      },
];

const FORMATS = ['paperback', 'hardcover', 'audiobook', 'ebook'];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(r.result);
    r.onerror = () => reject(new Error('Read failed'));
    r.readAsDataURL(file);
  });
}

export default function BowkerWizard({ record, onSaved, onExit }) {
  const [step, setStep]     = useState(0);
  const [busy, setBusy]     = useState(false);
  const [draft, setDraft]   = useState(() => ({ ...record }));
  const [coverBusy, setCoverBusy] = useState(false);

  useEffect(() => { setDraft({ ...record }); setStep(0); }, [record]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const savePatch = async (patch) => {
    setBusy(true);
    try {
      // Send only the keys IsbnMetadata accepts; unknown fields are
      // silently dropped by the backend, so this is safe.
      await isbnApi.updateMine(record.id, patch);
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const next = async () => {
    const patches = {
      basics: {
        title:        draft.title || '',
        subtitle:     draft.subtitle || null,
        contributors: (draft._contributors_text ?? (draft.contributors || []).join(', '))
                        .split(',').map((s) => s.trim()).filter(Boolean),
        format:       draft.format || null,
      },
      physical: {
        trim_size:  draft.trim_size || null,
        page_count: Number(draft.page_count) || null,
        edition:    draft.edition || null,
        language:   draft.language || null,
      },
      categories: {
        bisac_codes: (draft._bisac_text ?? (draft.bisac_codes || []).join(', '))
                        .split(',').map((s) => s.trim()).filter(Boolean),
        publication_date: draft.publication_date || null,
      },
      retail: {
        retail_price_cents: Math.max(0, Math.round(Number(draft._retail_dollars ?? (record.retail_price_cents == null ? 0 : record.retail_price_cents / 100)) * 100)),
        currency:           (draft.currency || 'USD').toUpperCase(),
      },
      cover: {},   // handled by dedicated endpoint below
    }[STEPS[step].key];

    try {
      if (Object.keys(patches || {}).length > 0) await savePatch(patches);
      if (step < STEPS.length - 1) setStep(step + 1);
      else { toast.success('Bowker submission complete.'); onExit?.(); }
    } catch { /* toast already shown */ }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Cover must be under 2 MB.');
      return;
    }
    setCoverBusy(true);
    try {
      const data_url = await fileToDataUrl(file);
      await isbnApi.uploadCover(record.id, { data_url, filename: file.name });
      await onSaved?.();
      toast.success('Cover uploaded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setCoverBusy(false);
    }
  };

  const removeCover = async () => {
    setCoverBusy(true);
    try {
      await isbnApi.deleteCover(record.id);
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed.');
    } finally {
      setCoverBusy(false);
    }
  };

  const progress = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  return (
    <div className="flex h-full flex-col" data-testid="bowker-wizard">
      {/* Stepper */}
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Bowker wizard · Step {step + 1} of {STEPS.length}</span>
          <span className="font-mono-stat">{progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-[10.5px]">
          {STEPS.map((s, i) => (
            <span key={s.key}
                  className={`rounded-full border px-2 py-[1.5px] ${i === step ? 'border-primary bg-primary/10 text-primary font-semibold' : i < step ? 'border-emerald-400/50 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20' : 'border-border text-muted-foreground'}`}
                  data-testid={`bowker-step-${s.key}`}>
              {i < step ? <Check className="mr-0.5 inline h-3 w-3" /> : null}{s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 space-y-3">
        {STEPS[step].key === 'basics' && (
          <>
            <FieldRow label="Title (required)">
              <Input value={draft.title || ''} onChange={(e) => set('title', e.target.value)}
                      data-testid="bw-title" placeholder="Manuscript title" />
            </FieldRow>
            <FieldRow label="Subtitle">
              <Input value={draft.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)}
                      data-testid="bw-subtitle" placeholder="Optional" />
            </FieldRow>
            <FieldRow label="Contributors (comma separated)">
              <Input
                value={draft._contributors_text ?? (draft.contributors || []).join(', ')}
                onChange={(e) => set('_contributors_text', e.target.value)}
                data-testid="bw-contributors"
                placeholder="e.g. Jane Doe (Author), John Smith (Editor)"
              />
            </FieldRow>
            <FieldRow label="Format">
              <select
                value={draft.format || 'paperback'}
                onChange={(e) => set('format', e.target.value)}
                data-testid="bw-format"
                className="h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
              >
                {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </FieldRow>
          </>
        )}

        {STEPS[step].key === 'physical' && (
          <>
            <FieldRow label="Trim size (in.)">
              <Input value={draft.trim_size || ''} onChange={(e) => set('trim_size', e.target.value)}
                      placeholder="e.g. 6 x 9" data-testid="bw-trim" />
            </FieldRow>
            <FieldRow label="Page count">
              <Input type="number" min="1" value={draft.page_count || ''} onChange={(e) => set('page_count', e.target.value)}
                      data-testid="bw-page-count" />
            </FieldRow>
            <FieldRow label="Edition">
              <Input value={draft.edition || ''} onChange={(e) => set('edition', e.target.value)}
                      placeholder="First" data-testid="bw-edition" />
            </FieldRow>
            <FieldRow label="Language">
              <Input value={draft.language || 'English'} onChange={(e) => set('language', e.target.value)}
                      data-testid="bw-language" />
            </FieldRow>
          </>
        )}

        {STEPS[step].key === 'categories' && (
          <>
            <FieldRow label="BISAC codes (comma separated)">
              <Input value={draft._bisac_text ?? (draft.bisac_codes || []).join(', ')}
                      onChange={(e) => set('_bisac_text', e.target.value)}
                      placeholder="e.g. FIC027000, FIC031000"
                      data-testid="bw-bisac" />
            </FieldRow>
            <FieldRow label="Publication date">
              <Input type="date" value={(draft.publication_date || '').slice(0, 10)}
                      onChange={(e) => set('publication_date', e.target.value)}
                      data-testid="bw-pub-date" />
            </FieldRow>
          </>
        )}

        {STEPS[step].key === 'retail' && (
          <>
            <FieldRow label="Retail price">
              <Input type="number" step="0.01" min="0"
                      value={draft._retail_dollars ?? (record.retail_price_cents == null ? '' : (record.retail_price_cents / 100).toFixed(2))}
                      onChange={(e) => set('_retail_dollars', e.target.value)}
                      data-testid="bw-price" />
            </FieldRow>
            <FieldRow label="Currency">
              <Input value={draft.currency || 'USD'} onChange={(e) => set('currency', e.target.value.toUpperCase())}
                      maxLength={3} data-testid="bw-currency" />
            </FieldRow>
          </>
        )}

        {STEPS[step].key === 'cover' && (
          <div className="space-y-3">
            <div className="text-[11.5px] text-muted-foreground">
              Upload a print-ready JPG or PNG (max 2 MB). We'll ship it to Bowker with the title record.
            </div>
            {record.cover_data_url ? (
              <div className="flex items-center gap-3 rounded-md border border-border p-2">
                <img src={record.cover_data_url} alt="Cover preview"
                      className="h-24 w-auto rounded-sm border border-border" data-testid="bw-cover-preview" />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-1 text-[12px] font-medium">{record.cover_filename || 'cover.jpg'}</div>
                  <div className="text-[10.5px] text-muted-foreground">
                    {(record.cover_bytes / 1024).toFixed(0)} KB · {record.cover_mime}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={removeCover} disabled={coverBusy} data-testid="bw-cover-remove">
                  <X className="h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border bg-secondary/50 px-3 py-3 text-[12px] hover:bg-secondary">
                {coverBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>{coverBusy ? 'Uploading…' : 'Click to choose a JPG/PNG'}</span>
                <input type="file" className="hidden" accept="image/jpeg,image/png"
                        onChange={(e) => uploadCover(e.target.files?.[0])}
                        data-testid="bw-cover-input" />
              </label>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        <div className="flex items-center gap-2">
          {step > 0 && (
            <Button size="sm" variant="outline" onClick={() => setStep(step - 1)} disabled={busy} data-testid="bw-back">
              <ChevronLeft className="h-3.5 w-3.5" /> Back
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onExit} data-testid="bw-exit">
            Exit wizard
          </Button>
          {/* Feb-2026 · Author-facing ONIX 3.0 download — mirrors the
           * admin bulk export but scoped to this single record. Enabled
           * once the wizard has a title on file. */}
          <a
            href={isbnApi.onixUrl(record.id)}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2.5 py-1 text-[11.5px] font-semibold hover:bg-secondary ${!draft.title ? 'pointer-events-none opacity-50' : ''}`}
            data-testid="bw-onix-download"
            title="Download an ONIX 3.0 XML you can submit directly to Bowker"
          >
            <FileDown className="h-3.5 w-3.5" /> ONIX 3.0
          </a>
        </div>
        <Button size="sm" onClick={next} disabled={busy} data-testid="bw-next">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {step === STEPS.length - 1 ? 'Finish' : <>Next <ChevronRight className="h-3.5 w-3.5" /></>}
        </Button>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
